import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getModelIdForProvider, providerForAiRequest, readAiSettings } from '../../../../ai/settings'
import type {
  CfoInsightsResponse,
  CfoReportRevisionSummary,
  MetricComparisonResponse,
  QuickBooksCfoPresentationResponse,
  ServerApiClient,
} from '../../../../data/ServerApiClient'
import { buildHighlightEvidence, reportingPeriodState } from '../visuals/evidence'
import { selectKeyHighlights, withCurrentPriorityCandidates } from '../visuals/selection'
import { resolvePresentationTheme } from '../../quickBooksCfoPresentationThemes'
import {
  DEFAULT_PRESENTATION_PERIOD_COUNT,
  QUARTERLY_TREND_MONTH_COUNT,
  SECTION_DEFS,
  balanceSheetRollupRows,
  cashFlowRollupRows,
  cleanText,
  loadSavedStatementViews,
  normalizeConfig,
  normalizeReportBlocks,
  normalizeSectionConfig,
  normalizeStringArray,
  presentationReportTitle,
  profitAndLossRollupRows,
  sourcePackageName,
  statementLatestPeriod,
  type PresentationAgentTarget,
  type PresentationConfig,
  type PresentationReportBlock,
  type PresentationSectionKey,
  type SavedStatementView,
} from '../../QuickBooksCfoPresentationTab'
import {
  buildQuickBooksCfoPresentationDeckHtml,
  buildQuickBooksCfoPresentationHtml,
  buildQuickBooksCfoPresentationPdfHtml,
} from '../../QuickBooksCfoPresentationExport'

// usePresentationStudio — the orchestration behind the calm-shell board-pack
// studio (`CfoPresentationsPage`). It owns data fetch, the config draft, the
// AI draft / agent-edit / undo flows, the export handlers, and inline
// commentary editing. The render (calm-shell chrome + WYSIWYG preview) lives in
// the page; this hook is the engine. Flows mirror the legacy
// `QuickBooksCfoPresentationTab` Main component so behaviour stays identical.

const DEFAULT_TOP_CUSTOMER_LIMIT = 5
// These production capabilities are on by default after contract acceptance;
// operations can still disable either one immediately with an explicit false
// kill switch. Keeping DEV as an implicit opt-in left the shipped cockpit with
// no highlights or export/version actions unless a deployment happened to
// inject an undocumented build-time variable.
const LIVE_KEY_HIGHLIGHTS_ENABLED = String(import.meta.env.VITE_CFO_LIVE_HIGHLIGHTS_ENABLED ?? 'true').toLowerCase() !== 'false'
const REPORT_MATERIALIZATION_ENABLED = String(import.meta.env.VITE_CFO_REPORT_MATERIALIZATION_ENABLED ?? 'true').toLowerCase() !== 'false'
const REPORT_AI_EDITOR_ENABLED = import.meta.env.DEV
  || String(import.meta.env.VITE_CFO_REPORT_AI_EDITOR_ENABLED || '').toLowerCase() === 'true'
// Bump when any durable HTML/PPTX materialization changes. Cached artifacts
// are keyed by this value, so a renderer fix must never hand users an older
// byte-identical version for the same captured report revision.
const REPORT_RENDERER_VERSION = 'live-board-report-v5'
const REPORT_THEME_VERSION = 'board-report-theme-v1'

// React development remounts effects to detect unsafe side effects. Without an
// in-flight join, a cockpit open launches the same governed presentation query
// twice and the discarded request competes with the request that paints the
// report. This is deliberately not a response cache: it only coalesces
// concurrent identical opens and is removed as soon as the request settles.
const inFlightPresentationLoads = new WeakMap<
  ServerApiClient,
  Map<string, Promise<QuickBooksCfoPresentationResponse>>
>()

const loadPresentationForFirstPaint = (
  appApi: ServerApiClient,
  workbookId: string,
): Promise<QuickBooksCfoPresentationResponse> => {
  let loads = inFlightPresentationLoads.get(appApi)
  if (!loads) {
    loads = new Map()
    inFlightPresentationLoads.set(appApi, loads)
  }
  const existing = loads.get(workbookId)
  if (existing) return existing
  const request = appApi
    .getCfoPresentation(workbookId, { includeArAnalysis: false })
    .finally(() => {
      if (loads?.get(workbookId) === request) loads.delete(workbookId)
    })
  loads.set(workbookId, request)
  return request
}

const contentHash = async (value: Blob | string): Promise<string> => {
  const bytes = typeof value === 'string' ? new TextEncoder().encode(value) : new Uint8Array(await value.arrayBuffer())
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

const blobBase64 = async (blob: Blob): Promise<string> => {
  const bytes = new Uint8Array(await blob.arrayBuffer())
  let binary = ''
  const chunkSize = 0x8000
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize))
  }
  return btoa(binary)
}

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export type StudioOutput = 'report' | 'slides' | 'share'

export type PresentationStudio = ReturnType<typeof usePresentationStudio>

export function usePresentationStudio(
  appApi: ServerApiClient,
  workbookId: string | null,
  isEditor = true,
) {
  // The shell can replace its API facade as authentication/bootstrap context
  // settles. That must not restart an already-governed report open for the same
  // workbook; later actions still read the current facade through this ref.
  const appApiRef = useRef(appApi)
  appApiRef.current = appApi
  const [response, setResponse] = useState<QuickBooksCfoPresentationResponse | null>(null)
  const [metricComparisons, setMetricComparisons] = useState<MetricComparisonResponse | null>(null)
  const [insights, setInsights] = useState<CfoInsightsResponse | null>(null)
  const [config, setConfig] = useState<PresentationConfig>(normalizeConfig(null))
  const [activeSection, setActiveSection] = useState<string>('home')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [savedViews, setSavedViews] = useState<SavedStatementView[]>([])
  const [aiDrafting, setAiDrafting] = useState(false)
  const [agentMessage, setAgentMessage] = useState('')
  const [agentBusy, setAgentBusy] = useState(false)
  const [agentError, setAgentError] = useState('')
  const [agentTarget, setAgentTarget] = useState<PresentationAgentTarget | null>(null)
  const [lastAgentRunId, setLastAgentRunId] = useState('')
  const [lastAgentSummary, setLastAgentSummary] = useState('')
  // Revisions are immutable and append-only. selectedPeriod '' = live report;
  // otherwise it contains the exact revision id being viewed read-only.
  const [revisions, setRevisions] = useState<CfoReportRevisionSummary[]>([])
  const [selectedPeriod, setSelectedPeriod] = useState<string>('')
  const [draftCanEdit, setDraftCanEdit] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [rollingForward, setRollingForward] = useState(false)
  const arHydrationKeyRef = useRef('')
  const previousHighlightsRef = useRef<NonNullable<QuickBooksCfoPresentationResponse['key_highlights']>>([])
  const selectionTelemetryDigestRef = useRef('')

  const applyPresentationResponse = useCallback(
    (next: QuickBooksCfoPresentationResponse, preserveActiveSection = false) => {
      if (next.key_highlights?.length) previousHighlightsRef.current = next.key_highlights
      setResponse(next)
      setInsights(next.insights || null)
      setMetricComparisons(next.metric_comparisons || null)
      const nextConfig = normalizeConfig(next.config)
      setConfig(nextConfig)
      const firstEnabled = SECTION_DEFS.find((section) => nextConfig.sections?.[section.key] !== false)?.key || 'home'
      setActiveSection((prev) => (
        preserveActiveSection && nextConfig.sections?.[prev] !== false ? prev : firstEnabled
      ))
    },
    [],
  )

  useEffect(() => {
    let cancelled = false
    const requestApi = appApiRef.current
    previousHighlightsRef.current = []
    selectionTelemetryDigestRef.current = ''
    setLoading(true)
    setError(null)
    if (!workbookId) {
      setLoading(false)
      setError('Workbook context is unavailable.')
      return () => {
        cancelled = true
      }
    }
    void (async () => {
      try {
        const next = await loadPresentationForFirstPaint(requestApi, workbookId)
        if (cancelled) return
        applyPresentationResponse(next)
        setDraftCanEdit(Boolean(next.can_edit))
      } catch (err: any) {
        if (cancelled) return
        setError(cleanText(err?.message || err) || 'Client financials are unavailable.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    // Immutable version history (non-fatal if it fails).
    void (async () => {
      try {
        const res = await requestApi.listCfoReportRevisions(workbookId)
        if (!cancelled) setRevisions(Array.isArray(res?.revisions) ? res.revisions : [])
      } catch {
        if (!cancelled) setRevisions([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [applyPresentationResponse, workbookId])

  useEffect(() => {
    if (selectedPeriod) return
    // The canonical presentation request owns the first paint and already
    // includes ranked insights. Starting the legacy insights endpoint while
    // that request is still in flight duplicated the governed statement/ratio
    // pipeline and competed with the report open. Only use it as a true
    // compatibility fallback after the presentation response has completed.
    if (loading || !response) return
    if (response?.insights) {
      setInsights(response.insights)
      return
    }
    if (!workbookId || typeof appApi.getCfoInsights !== 'function') {
      setInsights(null)
      return
    }
    let cancelled = false
    void appApi.getCfoInsights(workbookId)
      .then((next) => { if (!cancelled) setInsights(next) })
      .catch(() => { if (!cancelled) setInsights(null) })
    return () => { cancelled = true }
  }, [appApi, loading, response, selectedPeriod, workbookId])

  useEffect(() => {
    if (selectedPeriod || !workbookId || !response || response.ar_analysis) return
    const needsArAnalysis = normalizeReportBlocks(config.reportBlocks).some(
      (block) => block.enabled !== false && block.kind === 'ar_summary',
    )
    if (!needsArAnalysis || typeof appApi.getCfoPresentationArAnalysis !== 'function') return
    const hydrationKey = `${workbookId}:${response.binding_id}:${response.generated_at || response.layout_version_id}`
    if (arHydrationKeyRef.current === hydrationKey) return
    arHydrationKeyRef.current = hydrationKey
    let cancelled = false
    let timer: number | null = null
    const load = () => {
      void (async () => {
        try {
          const ar = await appApi.getCfoPresentationArAnalysis(workbookId)
          if (cancelled) return
          setResponse((current) => current && current.workbook_id === response.workbook_id
            ? {
                ...current,
                ar_analysis: ar.ar_analysis || null,
                warnings: [...new Set([...(current.warnings || []), ...(ar.warnings || [])])],
              }
            : current)
        } catch {
          // A/R analysis is a lower-section enrichment; the core board pack stays rendered.
        }
      })()
    }
    const idle = typeof window !== 'undefined' ? (window as any).requestIdleCallback : null
    if (typeof idle === 'function') {
      const id = idle(load, { timeout: 1000 })
      return () => {
        cancelled = true
        const cancelIdle = (window as any).cancelIdleCallback
        if (typeof cancelIdle === 'function') cancelIdle(id)
      }
    }
    if (typeof window === 'undefined') {
      load()
      return () => {
        cancelled = true
      }
    }
    timer = window.setTimeout(load, 100)
    return () => {
      cancelled = true
      if (timer != null) window.clearTimeout(timer)
    }
  }, [appApi, config.reportBlocks, response, selectedPeriod, workbookId])

  // Canonical comparison data only enriches waterfall bridge labels. Defer it
  // until the core report has rendered so a missing/stale analytics package
  // cannot compete with the first board-pack paint.
  useEffect(() => {
    if (response?.metric_comparisons) {
      setMetricComparisons(response.metric_comparisons)
      return
    }
    if (selectedPeriod) return
    if (!workbookId || !response || typeof appApi.getCfoMetricComparisons !== 'function') {
      setMetricComparisons(null)
      return
    }
    let cancelled = false
    let timer: number | null = null
    const load = () => {
      void (async () => {
        try {
          const cmp = await appApi.getCfoMetricComparisons(workbookId, { grain: 'month' })
          if (!cancelled) setMetricComparisons(cmp)
        } catch {
          if (!cancelled) setMetricComparisons(null)
        }
      })()
    }
    const idle = typeof window !== 'undefined' ? (window as any).requestIdleCallback : null
    if (typeof idle === 'function') {
      const id = idle(load, { timeout: 1500 })
      return () => {
        cancelled = true
        const cancelIdle = (window as any).cancelIdleCallback
        if (typeof cancelIdle === 'function') cancelIdle(id)
      }
    }
    if (typeof window === 'undefined') {
      load()
      return () => {
        cancelled = true
      }
    }
    timer = window.setTimeout(load, 250)
    return () => {
      cancelled = true
      if (timer != null) window.clearTimeout(timer)
    }
  }, [appApi, response, selectedPeriod, workbookId])

  const canEdit = Boolean(isEditor && response?.can_edit)

  useEffect(() => {
    setSavedViews(loadSavedStatementViews(cleanText(response?.connection_id)))
  }, [response?.connection_id])

  /* AI commentary is now explicit and event-driven. Opening or refreshing the
   * report never spends an AI call; the existing Ask AI actions remain the
   * editorial path. */
  /*
  useEffect(() => {
    if (!workbookId || !response || !canEdit) return
    const normalized = normalizeConfig(config)
    const existingSummary = cleanText(normalized.commentary?.executiveSummary)
    const existingActions = normalizeStringArray(normalized.commentary?.actions)
    if (existingSummary || existingActions.length) return
    const draftKey = `${workbookId}:${response.binding_id}:${response.generated_at || response.layout_version_id}`
    if (autoAiDraftKeyRef.current === draftKey) return
    autoAiDraftKeyRef.current = draftKey
    let cancelled = false
    void (async () => {
      const aiSettings = readAiSettings()
      const provider = aiSettings.provider
      const modelId = getModelIdForProvider(aiSettings, provider)
      const requestProvider = providerForAiRequest(provider)
      setAiDrafting(true)
      setStatusMessage('Drafting AI analysis from your financials…')
      try {
        const draft = await appApi.generateQuickBooksCfoPresentationAiDraft(workbookId, {
          provider: requestProvider,
          model_id: requestProvider ? cleanText(modelId) || null : null,
          confirmed_share_evidence: true,
          sections: Object.entries(normalizeSectionConfig(normalized.sectionConfig, normalized.sections || {}))
            .filter(([, sectionCfg]) => sectionCfg.aiEnabled !== false)
            .map(([id, sectionCfg]) => ({
              id,
              title: sectionCfg.title,
              prompt: sectionCfg.defaultPrompt,
              promptHints: sectionCfg.promptHints,
              promptAvoid: sectionCfg.promptAvoid,
            })),
        })
        if (cancelled) return
        const nextConfig = normalizeConfig({
          ...normalized,
          commentary: {
            ...normalized.commentary,
            executiveSummary: cleanText(draft.executiveSummary),
            sectionSummaries: Object.fromEntries(
              Object.entries((draft as any).sectionSummaries || {})
                .map(([key, value]) => [key, cleanText(value)])
                .filter(([, value]) => value),
            ),
            actions: normalizeStringArray(draft.actions),
            aiMeta: {
              model_id: cleanText(draft.model_id),
              generated_at: cleanText(draft.generated_at),
              evidence_count: Array.isArray(draft.evidence) ? draft.evidence.length : 0,
              warnings: normalizeStringArray(draft.warnings),
            },
          },
        })
        const next = await appApi.updateQuickBooksCfoPresentation(workbookId, { presentation: nextConfig })
        if (cancelled) return
        applyPresentationResponse(next, true)
        setStatusMessage('AI analysis drafted from the rendered financial statements.')
      } catch (err: any) {
        if (!cancelled) {
          setStatusMessage(`AI draft unavailable: ${cleanText(err?.message || err) || 'Could not generate analysis.'}`)
        }
      } finally {
        if (!cancelled) setAiDrafting(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [appApi, applyPresentationResponse, canEdit, config, response, workbookId])
  */

  useEffect(() => {
    if (!LIVE_KEY_HIGHLIGHTS_ENABLED || selectedPeriod || !response || !insights) return
    // An open reporting period disqualifies period-over-period findings: the
    // costs are posted and the revenue is not, so every MoM move reads as a
    // collapse. Resolved once from the governed data-through date and passed to
    // both the candidate filler and the selector.
    const nowUtc = new Date()
    const { periodOpen } = reportingPeriodState(response, { year: nowUtc.getUTCFullYear(), month: nowUtc.getUTCMonth() + 1 })
    const candidateCards = withCurrentPriorityCandidates(insights.cards || [], response.metric_comparisons, 3, { periodOpen })
    const visualEvidence = buildHighlightEvidence(response, candidateCards)
    const selection = selectKeyHighlights({
      insights: candidateCards,
      evidence: visualEvidence,
      previous: previousHighlightsRef.current,
      baseline: response.review_baseline || undefined,
      periodOpen,
    })
    const sourceSignature = response.source_state?.signature || `${insights.signature}:${response.layout_version_id}`
    const digest = `${sourceSignature}:${selection.highlights.map((entry) => `${entry.id}:${entry.templateId}:${entry.evidenceHash}`).join('|')}`
    const currentDigest = `${response.source_state?.signature || ''}:${(response.key_highlights || []).map((entry) => `${entry.id}:${entry.templateId}:${entry.evidenceHash}`).join('|')}`
    if (digest === currentDigest) return
    previousHighlightsRef.current = selection.highlights
    setResponse((current) => current ? {
      ...current,
      source_state: {
        ...(current.source_state || {}),
        signature: sourceSignature,
        reporting_period: current.source_state?.reporting_period || insights.as_of,
        data_through: current.source_state?.data_through || insights.as_of,
        last_successful_refresh: current.source_state?.last_successful_refresh || current.generated_at,
        reconciliation_status: current.source_state?.reconciliation_status || 'needs_review',
        close_readiness_status: current.source_state?.close_readiness_status || 'needs_review',
        forecast_included: current.source_state?.forecast_included || false,
        new_data_available: false,
        review_state: current.source_state?.review_state || 'needs_review',
      },
      visual_evidence: visualEvidence,
      key_highlights: selection.highlights,
      visual_selection_diagnostics: { rejections: selection.rejections },
      visual_selection_version: selection.visualSelectionVersion,
      visual_evidence_schema_version: selection.visualEvidenceSchemaVersion,
    } : current)
    if (workbookId && selectionTelemetryDigestRef.current !== digest) {
      selectionTelemetryDigestRef.current = digest
      void appApi.recordCfoHighlightSelection(workbookId, {
        source_signature: sourceSignature,
        visual_selection_version: selection.visualSelectionVersion,
        visual_evidence_schema_version: selection.visualEvidenceSchemaVersion,
        selected: selection.highlights,
        evidence: visualEvidence,
        rejections: selection.rejections,
      }).catch((error) => {
        const message = error instanceof Error ? error.message : 'Highlight selection persistence failed'
        console.warn('[CFO-HIGHLIGHTS] governed selection persistence failed', { workbookId, sourceSignature, message })
        setResponse((current) => current ? {
          ...current,
          visual_selection_diagnostics: {
            rejections: current.visual_selection_diagnostics?.rejections || selection.rejections,
            persistence_error: message,
          },
        } : current)
      })
    }
  }, [appApi, insights, response, selectedPeriod, workbookId])

  useEffect(() => {
    if (!workbookId || selectedPeriod || !response?.source_state?.signature) return
    let cancelled = false
    const poll = async () => {
      try {
        const latest = await appApi.getCfoPresentation(workbookId, { includeArAnalysis: false })
        if (cancelled || latest.source_state?.signature === response.source_state?.signature) return
        setResponse((current) => current ? {
          ...current,
          source_state: { ...current.source_state!, new_data_available: true },
        } : current)
      } catch {
        // Connectivity state is already communicated by the containing CFO shell.
      }
    }
    const timer = window.setInterval(() => { void poll() }, 60_000)
    return () => { cancelled = true; window.clearInterval(timer) }
  }, [appApi, response?.source_state?.signature, selectedPeriod, workbookId])

  const enabledSections = useMemo(
    () => SECTION_DEFS.filter(
      (section) => config.sections?.[section.key] !== false && config.sectionConfig?.[section.key]?.enabled !== false,
    ),
    [config.sectionConfig, config.sections],
  )

  const commentary = config.commentary || {}
  const sectionSummaries = commentary.sectionSummaries || {}
  const metrics = response?.metrics || {}
  const statements = response?.statements
  const period = statementLatestPeriod(statements?.profit_and_loss)
  const alerts = normalizeStringArray(commentary.alerts)
  const actions = normalizeStringArray(commentary.actions)
  const caveats = normalizeStringArray(commentary.dataCaveats)
  const summary = cleanText(commentary.executiveSummary)
  const selectedPeriodCount = Math.max(
    1,
    Math.min(24, Math.floor(Number(config.periodCount) || DEFAULT_PRESENTATION_PERIOD_COUNT)),
  )
  const profitAndLossRollup = useMemo(
    () => profitAndLossRollupRows(statements?.profit_and_loss, selectedPeriodCount, config.derivedMetrics),
    [config.derivedMetrics, selectedPeriodCount, statements?.profit_and_loss],
  )
  const quarterlyProfitAndLossRollup = useMemo(
    () => profitAndLossRollupRows(statements?.profit_and_loss, QUARTERLY_TREND_MONTH_COUNT, config.derivedMetrics),
    [config.derivedMetrics, statements?.profit_and_loss],
  )
  const balanceSheetRollup = useMemo(
    () => balanceSheetRollupRows(statements?.balance_sheet, selectedPeriodCount),
    [selectedPeriodCount, statements?.balance_sheet],
  )
  const cashFlowRollup = useMemo(
    () => cashFlowRollupRows(statements?.cash_flow, selectedPeriodCount),
    [selectedPeriodCount, statements?.cash_flow],
  )
  const theme = useMemo(() => resolvePresentationTheme(config.style), [config.style])
  const topCustomerLimit = config.receivables?.topCustomerLimit || DEFAULT_TOP_CUSTOMER_LIMIT

  const refreshReport = useCallback(async () => {
    if (!workbookId || refreshing) return
    setRefreshing(true)
    setStatusMessage('Refreshing report…')
    try {
      const next = await appApi.getCfoPresentation(workbookId, { includeArAnalysis: false })
      applyPresentationResponse(next, true)
      setStatusMessage(`Refreshed from ${sourcePackageName(normalizeConfig(next.config), next)}.`)
    } catch (err: any) {
      setStatusMessage(`Refresh failed: ${cleanText(err?.message || err) || 'Could not refresh report.'}`)
    } finally {
      setRefreshing(false)
    }
  }, [appApi, applyPresentationResponse, refreshing, workbookId])

  // Persist the current draft (used by the inspector drawers' Save buttons).
  const saveConfig = useCallback(async () => {
    if (!workbookId || !canEdit) return
    setSaving(true)
    setError(null)
    try {
      const normalized = normalizeConfig(config)
      const next = await appApi.updateQuickBooksCfoPresentation(workbookId, { presentation: normalized })
      applyPresentationResponse(next, true)
    } catch (err: any) {
      setError(cleanText(err?.message || err) || 'Could not save presentation settings.')
    } finally {
      setSaving(false)
    }
  }, [appApi, applyPresentationResponse, canEdit, config, workbookId])

  // Persist an explicit next config (used by inline edits + curate actions so a
  // change is saved immediately without relying on the latest `config` state).
  const commitConfig = useCallback(
    async (nextConfig: PresentationConfig) => {
      const normalized = normalizeConfig(nextConfig)
      setConfig(normalized)
      if (!workbookId || !canEdit) return
      setSaving(true)
      setError(null)
      try {
        const next = await appApi.updateQuickBooksCfoPresentation(workbookId, { presentation: normalized })
        applyPresentationResponse(next, true)
      } catch (err: any) {
        setError(cleanText(err?.message || err) || 'Could not save your change.')
      } finally {
        setSaving(false)
      }
    },
    [appApi, applyPresentationResponse, canEdit, workbookId],
  )

  // Inline commentary editors (close the "section summaries are AI-only / text
  // bodies uneditable" gap). Each updates the canonical commentary store / block
  // content and persists.
  const setExecutiveSummary = useCallback(
    (text: string) => commitConfig({
      ...config,
      commentary: { ...(config.commentary || {}), executiveSummary: text },
    }),
    [commitConfig, config],
  )
  const setSectionSummary = useCallback(
    (sectionKey: string, text: string) => commitConfig({
      ...config,
      commentary: {
        ...(config.commentary || {}),
        sectionSummaries: { ...(config.commentary?.sectionSummaries || {}), [sectionKey]: text },
      },
    }),
    [commitConfig, config],
  )
  const setBlockText = useCallback(
    (blockId: string, text: string) => {
      const blocks = normalizeReportBlocks(config.reportBlocks).map((block) => (
        block.id === blockId ? { ...block, content: { ...(block.content || {}), text } } : block
      ))
      return commitConfig({ ...config, reportBlocks: blocks })
    },
    [commitConfig, config],
  )
  const setCommentaryList = useCallback(
    (key: 'alerts' | 'actions' | 'dataCaveats', items: string[]) => commitConfig({
      ...config,
      commentary: { ...(config.commentary || {}), [key]: items },
    }),
    [commitConfig, config],
  )

  // ----- Exports (mirror the legacy tab's robust paths) -----------------
  const printHtmlDocument = useCallback((html: string, safeTitle: string) => {
    const printWindow = window.open('', `${safeTitle}-pdf`, 'width=1100,height=900')
    if (printWindow) {
      printWindow.document.open()
      printWindow.document.write(html)
      printWindow.document.close()
      printWindow.document.title = safeTitle
      const printPacket = () => {
        try {
          printWindow.focus()
          printWindow.print()
        } catch {
          setStatusMessage('PDF export could not open the print dialog.')
        }
      }
      if (printWindow.document.readyState === 'complete') {
        window.setTimeout(printPacket, 120)
      } else {
        printWindow.addEventListener('load', () => window.setTimeout(printPacket, 120), { once: true })
      }
      setStatusMessage('PDF print dialog opened. Choose "Save as PDF".')
      return
    }
    const iframe = document.createElement('iframe')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0'
    document.body.appendChild(iframe)
    const doc = iframe.contentWindow?.document
    if (!doc) {
      iframe.remove()
      setStatusMessage('PDF export is unavailable in this browser context.')
      return
    }
    doc.open()
    doc.write(html)
    doc.close()
    const printFrame = () => {
      try {
        iframe.contentWindow?.focus()
        iframe.contentWindow?.print()
        setStatusMessage('PDF print dialog opened. Choose "Save as PDF".')
      } catch {
        setStatusMessage('PDF export could not open the print dialog.')
      } finally {
        window.setTimeout(() => iframe.remove(), 1000)
      }
    }
    if (doc.readyState === 'complete') window.setTimeout(printFrame, 150)
    else iframe.addEventListener('load', () => window.setTimeout(printFrame, 150), { once: true })
  }, [])

  const safeReportTitle = useCallback(
    () => presentationReportTitle(config).replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-|-$/g, '') || 'financial-review',
    [config],
  )

  const reuseMaterialization = useCallback(async (
    revisionId: string,
    format: 'html' | 'presentation',
    fileName: string,
  ): Promise<boolean> => {
    if (!REPORT_MATERIALIZATION_ENABLED || !workbookId) return false
    const artifacts = await appApi.listCfoReportMaterializations(workbookId, revisionId)
    const ready = artifacts.find((artifact) => artifact.format === format
      && artifact.status === 'ready'
      && artifact.renderer_version === REPORT_RENDERER_VERSION
      && artifact.theme_version === REPORT_THEME_VERSION)
    if (!ready) return false
    const blob = await appApi.downloadCfoReportMaterialization(workbookId, revisionId, ready.id)
    downloadBlob(blob, fileName)
    return true
  }, [appApi, workbookId])

  const storeMaterialization = useCallback(async (
    revisionId: string,
    format: 'html' | 'presentation',
    blob: Blob,
  ) => {
    if (!REPORT_MATERIALIZATION_ENABLED || !workbookId) return
    const hash = await contentHash(blob)
    const mimeType = format === 'html'
      ? 'text/html'
      : 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    await appApi.registerCfoReportMaterialization(workbookId, revisionId, {
      format,
      status: 'ready',
      content_hash: hash,
      mime_type: mimeType,
      byte_size: blob.size,
      renderer_version: REPORT_RENDERER_VERSION,
      theme_version: REPORT_THEME_VERSION,
      content_base64: await blobBase64(blob),
    })
  }, [appApi, workbookId])

  const captureCurrentRevision = useCallback(async (status: 'captured' | 'issued' = 'captured') => {
    if (selectedPeriod) {
      if (status === 'issued') throw new Error('This version is already immutable. Return to the live report to issue a new version.')
      if (!workbookId) throw new Error('Workbook context is unavailable.')
      return await appApi.getCfoReportRevision(workbookId, selectedPeriod)
    }
    if (!workbookId || !response?.source_state?.signature) throw new Error('The live report source is not ready yet.')
    const snapshot = {
      ...response,
      // Revision capture is server-governed and validates the configuration
      // that produced the live response. Keep that wire shape intact here;
      // normalizeConfig expands browser rendering defaults and is not the
      // persisted configuration contract.
      config: response.config,
      insights,
      metric_comparisons: metricComparisons,
    }
    return await appApi.captureCfoReportRevision(workbookId, {
      source_signature: response.source_state.signature,
      reporting_period: response.source_state.reporting_period || period || null,
      snapshot,
      status,
    })
  }, [appApi, insights, metricComparisons, period, response, selectedPeriod, workbookId])

  const markReviewed = useCallback(async () => {
    if (!workbookId || !response?.source_state?.signature) return
    setStatusMessage('Marking the displayed report reviewed…')
    try {
      const checkpoint = await appApi.createCfoReviewCheckpoint(workbookId, {
        source_signature: response.source_state.signature,
        reporting_period: response.source_state.reporting_period || period || null,
        insight_digest: (insights?.cards || []).map((item) => `${item.id}:${item.rank}:${item.score}`).join('|'),
        highlight_digest: (response.key_highlights || []).map((item) => `${item.storyFingerprint}:${item.evidenceHash}`).join('|'),
        data_state: {
          source_state: response.source_state,
          highlights: response.key_highlights || [],
          insights: insights?.cards || [],
        },
      })
      setResponse((current) => current ? {
        ...current,
        source_state: {
          ...current.source_state!,
          review_state: 'reviewed',
          last_reviewed_at: checkpoint.reviewed_at || new Date().toISOString(),
          last_reviewed_by: checkpoint.reviewed_by || null,
          last_reviewer_name: checkpoint.reviewer_name || null,
        },
      } : current)
      setStatusMessage(`Reviewed ${checkpoint.reporting_period}. Future changes will compare with this checkpoint.`)
    } catch (err: any) {
      setStatusMessage(`Review failed: ${cleanText(err?.message || err) || 'Could not mark reviewed.'}`)
    }
  }, [appApi, insights, period, response, workbookId])

  const exportPdf = useCallback(async () => {
    if (!response) return
    let frozen = { ...response, config: normalizeConfig(config) }
    let revisionId = ''
    // Session-isolated demos are intentionally read-only. Their exports remain
    // real governed files, but must not require permission to persist a report
    // revision. Editable workbooks keep the immutable revision workflow.
    if (canEdit) {
      try { const captured = await captureCurrentRevision(); frozen = captured.snapshot; revisionId = captured.revision.id } catch (err: any) { setStatusMessage(cleanText(err?.message || err)); return }
    }
    const normalized = normalizeConfig(frozen.config)
    const html = buildQuickBooksCfoPresentationPdfHtml(frozen, frozen.metric_comparisons || metricComparisons)
    const reportTitle = presentationReportTitle(normalized)
    const safeTitle = safeReportTitle()
    if (workbookId && typeof appApi.renderCfoPresentationPdf === 'function') {
      setStatusMessage('Rendering PDF on the server…')
      try {
        const blob = await appApi.renderCfoPresentationPdf(workbookId, { html, title: reportTitle, revision_id: revisionId || null })
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = `${safeTitle}.pdf`
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        window.setTimeout(() => URL.revokeObjectURL(url), 1000)
        setStatusMessage(`Exported ${safeTitle}.pdf.`)
        return
      } catch {
        setStatusMessage('Server PDF unavailable — opening browser print instead.')
      }
    }
    printHtmlDocument(html, safeTitle)
  }, [appApi, canEdit, captureCurrentRevision, config, metricComparisons, printHtmlDocument, response, safeReportTitle, workbookId])

  const exportHtml = useCallback(async () => {
    if (!response) return
    let frozen = { ...response, config: normalizeConfig(config) }
    let revisionId = ''
    if (canEdit) {
      try { const captured = await captureCurrentRevision(); frozen = captured.snapshot; revisionId = captured.revision.id } catch (err: any) { setStatusMessage(cleanText(err?.message || err)); return }
    }
    const html = buildQuickBooksCfoPresentationHtml(frozen, frozen.metric_comparisons || metricComparisons)
    const safeTitle = safeReportTitle()
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
    try {
      if (revisionId && await reuseMaterialization(revisionId, 'html', `${safeTitle}.html`)) {
        setStatusMessage(`Downloaded the existing ${safeTitle}.html for this exact version.`)
        return
      }
      if (revisionId) await storeMaterialization(revisionId, 'html', blob)
    } catch {
      // A personal download remains available if durable storage is unavailable.
    }
    downloadBlob(blob, `${safeTitle}.html`)
    setStatusMessage(`Exported ${safeTitle}.html.`)
  }, [canEdit, captureCurrentRevision, config, metricComparisons, response, reuseMaterialization, safeReportTitle, storeMaterialization])

  const buildDeckHtml = useCallback(() => {
    if (!response) return ''
    return buildQuickBooksCfoPresentationDeckHtml({ ...response, config: normalizeConfig(config) }, metricComparisons)
  }, [config, metricComparisons, response])

  // Real PowerPoint export. The builder (and pptxgenjs) loads on demand so the
  // studio bundle stays lean; slides paginate via the same deck model as the
  // HTML deck.
  const exportPptx = useCallback(async () => {
    if (!response) return
    setStatusMessage('Building the PowerPoint deck…')
    let revisionId = ''
    try {
      let frozen = { ...response, config: normalizeConfig(config) }
      if (canEdit) {
        const captured = await captureCurrentRevision()
        revisionId = captured.revision.id
        frozen = captured.snapshot
      }
      const fileName = `${safeReportTitle()}.pptx`
      if (revisionId && await reuseMaterialization(revisionId, 'presentation', fileName)) {
        setStatusMessage('Downloaded the existing PowerPoint for this exact version.')
        return
      }
      if (REPORT_MATERIALIZATION_ENABLED && workbookId && revisionId) {
        await appApi.registerCfoReportMaterialization(workbookId, revisionId, {
          format: 'presentation', status: 'building', renderer_version: REPORT_RENDERER_VERSION, theme_version: REPORT_THEME_VERSION,
        })
      }
      const { exportPresentationPptx } = await import('./pptxExport')
      const slideCount = await exportPresentationPptx({
        response: frozen,
        comparisons: frozen.metric_comparisons || metricComparisons,
        fileName: presentationReportTitle(config),
        onBuilt: async (blob) => {
          if (!revisionId) return
          try { await storeMaterialization(revisionId, 'presentation', blob) } catch { /* personal download remains available */ }
        },
      })
      setStatusMessage(`Exported ${slideCount} slides to PowerPoint.`)
    } catch (err: any) {
      if (REPORT_MATERIALIZATION_ENABLED && workbookId && revisionId) {
        void appApi.registerCfoReportMaterialization(workbookId, revisionId, {
          format: 'presentation', status: 'failed', renderer_version: REPORT_RENDERER_VERSION, theme_version: REPORT_THEME_VERSION,
          error_message: cleanText(err?.message || err) || 'PowerPoint build failed',
        }).catch(() => {})
      }
      setStatusMessage(`PowerPoint export failed: ${cleanText(err?.message || err) || 'Could not build the deck.'}`)
    }
  }, [appApi, canEdit, captureCurrentRevision, config, metricComparisons, response, reuseMaterialization, safeReportTitle, storeMaterialization, workbookId])

  const openSlideDeck = useCallback(() => {
    const html = buildDeckHtml()
    if (!html) return
    const safeTitle = safeReportTitle()
    const deckWindow = window.open('', `${safeTitle}-deck`, 'width=1280,height=860')
    if (!deckWindow) {
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `${safeTitle}-slides.html`
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
      setStatusMessage('Allow pop-ups to view slides inline — downloaded the deck instead.')
      return
    }
    deckWindow.document.open()
    deckWindow.document.write(html)
    deckWindow.document.close()
    setStatusMessage('Slide deck opened. Use arrow keys or Print for PDF.')
  }, [buildDeckHtml, safeReportTitle])

  // ----- AI agent editing ------------------------------------------------
  const submitAgentEdit = useCallback(
    async (message: string, target: PresentationAgentTarget | null) => {
      if (!REPORT_AI_EDITOR_ENABLED || !workbookId || !canEdit || agentBusy) return
      const trimmed = cleanText(message)
      if (!trimmed) {
        setAgentError('Describe the report change to apply.')
        return
      }
      const aiSettings = readAiSettings()
      const provider = aiSettings.provider
      const modelId = getModelIdForProvider(aiSettings, provider)
      const requestProvider = providerForAiRequest(provider)
      setAgentBusy(true)
      setAgentError('')
      setStatusMessage('Applying AI report edit…')
      try {
        const result = await appApi.generateQuickBooksCfoPresentationAgentEdit(workbookId, {
          provider: requestProvider,
          model_id: requestProvider ? cleanText(modelId) || null : null,
          confirmed_share_evidence: true,
          message: trimmed,
          target_block_id: target?.blockId || null,
          target_section: target?.section || null,
        })
        applyPresentationResponse(result.presentation, true)
        setLastAgentRunId(cleanText(result.agent_run_id))
        setLastAgentSummary(cleanText(result.change_summary))
        setAgentMessage('')
        setAgentTarget(null)
        setStatusMessage(cleanText(result.change_summary) || 'AI report edit applied.')
        return true
      } catch (err: any) {
        const msg = cleanText(err?.message || err) || 'AI report edit failed.'
        setAgentError(msg)
        setStatusMessage(`AI report edit failed: ${msg}`)
        return false
      } finally {
        setAgentBusy(false)
      }
    },
    [agentBusy, appApi, applyPresentationResponse, canEdit, workbookId],
  )

  const undoLastAgentEdit = useCallback(async () => {
    if (!REPORT_AI_EDITOR_ENABLED || !workbookId || !canEdit || !lastAgentRunId || agentBusy) return
    setAgentBusy(true)
    setAgentError('')
    setStatusMessage('Undoing AI report edit…')
    try {
      const result = await appApi.undoQuickBooksCfoPresentationAgentRun(workbookId, lastAgentRunId)
      applyPresentationResponse(result.presentation, true)
      setLastAgentRunId('')
      setLastAgentSummary('')
      setStatusMessage(cleanText(result.change_summary) || 'AI report edit undone.')
    } catch (err: any) {
      const msg = cleanText(err?.message || err) || 'Could not undo AI report edit.'
      setAgentError(msg)
      setStatusMessage(`Undo failed: ${msg}`)
    } finally {
      setAgentBusy(false)
    }
  }, [agentBusy, appApi, applyPresentationResponse, canEdit, lastAgentRunId, workbookId])

  // ----- immutable revisions: issue / roll-forward / browse --------------
  const loadRevisions = useCallback(async () => {
    if (!workbookId) return
    try {
      const res = await appApi.listCfoReportRevisions(workbookId)
      setRevisions(Array.isArray(res?.revisions) ? res.revisions : [])
    } catch {
      /* non-fatal */
    }
  }, [appApi, workbookId])

  // Switch the viewed version. '' / 'draft' loads the live editable report;
  // any other value is an immutable revision id.
  const selectPeriod = useCallback(
    async (periodOrDraft: string) => {
      if (!workbookId) return
      if (!periodOrDraft || periodOrDraft === 'draft') {
        setSelectedPeriod('')
        try {
          const next = await appApi.getCfoPresentation(workbookId, { includeArAnalysis: false })
          applyPresentationResponse(next, true)
          setDraftCanEdit(Boolean(next.can_edit))
          setStatusMessage('Editing the current draft.')
        } catch (err: any) {
          setStatusMessage(`Could not load the draft: ${cleanText(err?.message || err)}`)
        }
        return
      }
      setSelectedPeriod(periodOrDraft)
      try {
        const revision = await appApi.getCfoReportRevision(workbookId, periodOrDraft)
        applyPresentationResponse(revision.snapshot, false)
        setStatusMessage(`Viewing ${revision.revision.label} (read-only and immutable).`)
      } catch (err: any) {
        setStatusMessage(`Could not open that version: ${cleanText(err?.message || err)}`)
      }
    },
    [appApi, applyPresentationResponse, workbookId],
  )

  const publish = useCallback(async () => {
    if (!workbookId || !draftCanEdit || publishing) return
    setPublishing(true)
    setStatusMessage('Creating an issued report version…')
    try {
      const result = await captureCurrentRevision('issued')
      await loadRevisions()
      setStatusMessage(`Issued ${result.revision.label}. It is frozen and will never be overwritten.`)
    } catch (err: any) {
      setStatusMessage(`Publish failed: ${cleanText(err?.message || err) || 'Could not publish.'}`)
    } finally {
      setPublishing(false)
    }
  }, [captureCurrentRevision, draftCanEdit, loadRevisions, publishing, workbookId])

  const rollForward = useCallback(async () => {
    if (!workbookId || !draftCanEdit || rollingForward) return
    setRollingForward(true)
    setStatusMessage('Rolling last month forward…')
    try {
      const next = await appApi.rollForwardQuickBooksCfoPresentation(workbookId, {})
      setSelectedPeriod('')
      applyPresentationResponse(next, true)
      setDraftCanEdit(Boolean(next.can_edit))
      await loadRevisions()
      setStatusMessage('Rolled forward. Edit the new month, then publish when ready.')
    } catch (err: any) {
      setStatusMessage(`Roll-forward failed: ${cleanText(err?.message || err) || 'Could not roll forward.'}`)
    } finally {
      setRollingForward(false)
    }
  }, [appApi, applyPresentationResponse, draftCanEdit, loadRevisions, rollingForward, workbookId])

  const viewingSnapshot = selectedPeriod !== ''
  const canManageMonths = Boolean(isEditor && draftCanEdit)

  const section = enabledSections.some((entry) => entry.key === activeSection)
    ? activeSection
    : enabledSections[0]?.key || 'home'
  const activeSectionKey = section as PresentationSectionKey
  const activeBlocks = useMemo(
    () => normalizeReportBlocks(config.reportBlocks).filter(
      (block) => block.enabled !== false && block.section === section,
    ),
    [config.reportBlocks, section],
  )

  return {
    // state
    response,
    config,
    setConfig,
    metricComparisons,
    insights,
    loading,
    error,
    canEdit,
    savedViews,
    saving,
    refreshing,
    aiDrafting,
    statusMessage,
    setStatusMessage,
    // sections / blocks
    enabledSections,
    activeSection: section,
    setActiveSection,
    activeSectionKey,
    activeBlocks,
    // derived
    metrics,
    statements,
    period,
    summary,
    alerts,
    actions,
    caveats,
    sectionSummaries,
    selectedPeriodCount,
    profitAndLossRollup,
    quarterlyProfitAndLossRollup,
    balanceSheetRollup,
    cashFlowRollup,
    theme,
    topCustomerLimit,
    // mutations
    applyPresentationResponse,
    saveConfig,
    commitConfig,
    setExecutiveSummary,
    setSectionSummary,
    setBlockText,
    setCommentaryList,
    refreshReport,
    captureCurrentRevision,
    markReviewed,
    // exports
    exportPdf,
    exportHtml,
    exportPptx,
    openSlideDeck,
    buildDeckHtml,
    // agent
    agentMessage,
    setAgentMessage,
    agentBusy,
    agentError,
    setAgentError,
    agentTarget,
    setAgentTarget,
    lastAgentRunId,
    lastAgentSummary,
    submitAgentEdit,
    undoLastAgentEdit,
    // versions
    revisions,
    selectedPeriod,
    viewingSnapshot,
    canManageMonths,
    publishing,
    rollingForward,
    selectPeriod,
    publish,
    rollForward,
    loadRevisions,
    reportMaterializationEnabled: REPORT_MATERIALIZATION_ENABLED,
    reportAiEditorEnabled: REPORT_AI_EDITOR_ENABLED,
  }
}

export type { PresentationConfig, PresentationReportBlock, PresentationSectionKey }
